// levelResources.ts — getting each compiled level its resources: the files the game makes from it, at
// the game's native 1302 x 2818, stored by the server (Controllers/LevelResourcesController.cs) with a
// small thumbnail each:
//   screenshot — the level as the game shows it, whole screen with its UI (RenderLevelImage);
//   walls      — the board's walls alone, transparent (the existing wall export, ExportWallPng).
// One load of the level gives both: RenderLevelImage leaves it loaded, and the wall export works on the
// level that is loaded.
//
// Shared by Game Levels ("Generate images") and Deployments (a publish fills in what Harbor lacks
// first). One run at a time across the whole page: the player can only show one level, and two loops
// would take turns loading boards under each other.
//
// HARBOR FIRST. Harbor keeps one resource per level name + content hash + kind, shared by every
// version. A kind it already has for a level is COPIED from it, not made again; only the rest go
// through the game. "Re-render all" is the exception — it exists to redraw after a new game build —
// and makes everything.
//
// While making them, the player is held in the PHONE frame whatever the Phone/iPad toggle says, and its
// backbuffer is pinned to 1302 x 2818, so every screenshot has the native shape and resolution.
import { LEVEL_RESOURCE_KINDS, fetchHarborResource, fetchHarborResourceAvailability, fetchLevelResourceStatus, getGameLevelContent, putLevelResource, } from "./ContentApi";
import { aspectStore } from "./aspectStore";
import { LEVEL_IMAGE_HEIGHT, LEVEL_IMAGE_WIDTH, unityBridge } from "./UnityBridge";
let busy = false;
/** True while some page is filling in level resources. */
export function isRenderingImages() {
    return busy;
}
/** The kinds a level needs: the ones that are not current, or all of them. */
function kindsNeeded(level, all) {
    return LEVEL_RESOURCE_KINDS.filter((k) => all || level.resources[k].state !== "current");
}
/** The levels a run would touch: those with any kind missing or outdated, or all of them. */
export function levelsToRender(status, all = false) {
    return status.filter((l) => kindsNeeded(l, all).length > 0);
}
// The frame resize is CSS and the backbuffer pin is immediate; Unity re-lays its UI on the next frames.
// Wait for that before the first picture.
const RESIZE_SETTLE_MS = 500;
// Thumbnails: the list shows them at 24 px wide; 120 px covers a 2x/3x screen with room to spare.
const THUMB_WIDTH = 120;
async function makeThumbnail(png) {
    const bitmap = await createImageBitmap(new Blob([png.slice().buffer], { type: "image/png" }));
    try {
        const width = THUMB_WIDTH;
        const height = Math.round((bitmap.height / bitmap.width) * width);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const g = canvas.getContext("2d");
        if (!g) {
            throw new Error("No 2D canvas to make the thumbnail with.");
        }
        g.imageSmoothingQuality = "high";
        g.drawImage(bitmap, 0, 0, width, height);
        return await new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not encode the thumbnail."))), "image/png"));
    }
    finally {
        bitmap.close();
    }
}
async function store(level, kind, png) {
    await putLevelResource(level.id, kind, level.jsonHash, png);
    await putLevelResource(level.id, kind, level.jsonHash, await makeThumbnail(png), true);
}
export async function renderLevelImages(target, options = {}) {
    if (busy) {
        throw new Error("Level resources are already being made — wait for that to finish.");
    }
    busy = true;
    const all = options.all === true;
    const result = { rendered: 0, copied: 0, skipped: 0, failed: [], stopped: false };
    let releaseAspect = null;
    let releaseBackbuffer = null;
    try {
        const status = await fetchLevelResourceStatus(target);
        // What Harbor already has, per level. Not having a Harbor connection is fine: everything is made.
        const onHarbor = new Map();
        if (!all) {
            try {
                for (const l of (await fetchHarborResourceAvailability(target)).levels) {
                    onHarbor.set(l.id, new Set(l.kinds));
                }
            }
            catch {
                onHarbor.clear();
            }
        }
        const plan = status
            .map((level) => {
            let kinds = kindsNeeded(level, all);
            if (options.onHarbor === "skip") {
                const harbor = onHarbor.get(level.id);
                const skipped = kinds.filter((k) => harbor?.has(k));
                result.skipped += skipped.length;
                kinds = kinds.filter((k) => !harbor?.has(k));
            }
            return { level, kinds };
        })
            .filter((p) => p.kinds.length > 0);
        for (let i = 0; i < plan.length; i++) {
            if (options.shouldStop?.()) {
                result.stopped = true;
                break;
            }
            const { level, kinds } = plan[i];
            options.onProgress?.({ done: i, total: plan.length, current: level.name, failed: result.failed.length });
            try {
                // Copy what Harbor has; whatever is left is made by the game.
                const toMake = [];
                for (const kind of kinds) {
                    let copied = false;
                    if (onHarbor.get(level.id)?.has(kind)) {
                        try {
                            await store(level, kind, await fetchHarborResource(level.id, kind));
                            result.copied++;
                            copied = true;
                        }
                        catch {
                            copied = false; // gone since the lookup, or Harbor unreachable — make it instead
                        }
                    }
                    if (!copied) {
                        toMake.push(kind);
                    }
                }
                if (toMake.length > 0) {
                    if (releaseAspect === null) {
                        // First render of the run: wait until the game answers (see waitUntilReady — the
                        // first request after a page load used to be dropped while the game still booted),
                        // then frame it and render one throwaway picture, so the first level kept is not
                        // the one drawn while the game loaded its art for the first time.
                        options.onProgress?.({ done: i, total: plan.length, current: level.name, failed: result.failed.length, waiting: true });
                        await unityBridge.waitUntilReady();
                        releaseAspect = aspectStore.isHeld ? () => { } : aspectStore.hold("phone");
                        releaseBackbuffer = unityBridge.holdBackbuffer(LEVEL_IMAGE_WIDTH, LEVEL_IMAGE_HEIGHT);
                        await new Promise((r) => window.setTimeout(r, RESIZE_SETTLE_MS));
                        options.onProgress?.({ done: i, total: plan.length, current: level.name, failed: result.failed.length });
                        await unityBridge.renderLevelImage(await getGameLevelContent(level.id));
                    }
                    // Loading the level IS the screenshot render; the walls are exported from it after.
                    const screenshot = await unityBridge.renderLevelImage(await getGameLevelContent(level.id));
                    if (toMake.includes("screenshot")) {
                        await store(level, "screenshot", screenshot);
                        result.rendered++;
                    }
                    if (toMake.includes("walls")) {
                        await store(level, "walls", await unityBridge.exportWallPngAsync(LEVEL_IMAGE_WIDTH, LEVEL_IMAGE_HEIGHT));
                        result.rendered++;
                    }
                }
            }
            catch (e) {
                result.failed.push({ name: level.name, error: e instanceof Error ? e.message : String(e) });
            }
            options.onProgress?.({ done: i + 1, total: plan.length, current: null, failed: result.failed.length });
        }
        return result;
    }
    finally {
        releaseBackbuffer?.();
        releaseAspect?.();
        busy = false;
    }
}
